/**
 * DownloadButton — единый переиспользуемый download-action для Files модуля.
 *
 * Phase 4 (Files UX Refactor — Export Redesign, 2026-08-08).
 *
 * Используется в трёх местах:
 *  - FilesResultsPage (per-row через variant="compact" в RowAction)
 *  - FilesTransformPage (action bar через variant="primary")
 *  - DataPreviewModal (footer через variant="primary")
 *
 * Гарантии:
 *  - Loading: isDownloading=true → aria-busy="true", иконка Loader2.
 *  - Disabled: dataset.status in (uploaded|parsing|failed) ||
 *    isTimeout=true → disabled + tooltip с локализованной причиной.
 *  - aria-label = aria-tooltip (одинаково для sighted и SR).
 *  - Error: внешний prop `error` (per-page state). Кнопка остаётся enabled
 *    после ошибки — пользователь должен иметь возможность повторить.
 *  - Backend contract (Phase 2.2) и `useDownloadDataset` hook не меняются.
 *  - Никакой Promise race на клик — `isDownloading` блокирует повторный
 *    клик на 3 уровнях (button.disabled + aria-busy + hook.isPending).
 *
 * Не сбрасывает `error` после успеха — это ответственность parent (он
 * инвалидирует query / размонтирует компонент).
 *
 * Filename contract: компонент НЕ вычисляет и НЕ подменяет filename —
 * парсится из Content-Disposition в `useDownloadDataset` (Phase 2.2 hook),
 * backend остаётся source of truth.
 */
import { useTranslation } from 'react-i18next';
import { Download, Loader2 } from 'lucide-react';

import { useParsingTimeout } from '@/hooks/useParsingTimeout';
import type { Dataset } from '@/hooks/useFiles';
import { cn } from '@/lib/utils';

export type DownloadButtonVariant = 'primary' | 'ghost' | 'compact';

export interface DownloadButtonProps {
  /**
   * Источник истины для логики disabled/tooltip.
   * Если null/undefined — кнопка disabled без причины (loading skeleton).
   */
  dataset: Dataset | null | undefined;
  /**
   * Callback, который вызывает `useDownloadDataset().mutateAsync(datasetId)`.
   * Parent отвечает за передачу правильного datasetId и за обработку ошибки.
   */
  onDownload: () => Promise<void> | void;
  isDownloading: boolean;
  variant?: DownloadButtonVariant;
  /** External inline error (per-page state). */
  error?: string | null;
  /** Override над default i18n-label (например, для «Скачать Excel»). */
  label?: string;
  /** data-testid — для smoke / Playwright. */
  testId?: string;
  /** Дополнительные className (например, для parent layout). */
  className?: string;
  /** if true — кнопка всегда disabled (например, для ready+empty dataset). */
  forceDisabled?: boolean;
}

export function DownloadButton({
  dataset,
  onDownload,
  isDownloading,
  variant = 'ghost',
  error,
  label,
  testId,
  className,
  forceDisabled,
}: DownloadButtonProps) {
  const { t } = useTranslation();

  // Phase 2.7: parsing-timeout signal. Если dataset застрял в parsing
  // > PARSING_TIMEOUT_MS, блокируем download (backend ещё не дал ready),
  // но НЕ превращаем в backend 'failed' — это UI-side signal.
  const isTimeout = useParsingTimeout(dataset ?? null);

  // === Disabled reason (приоритет сверху вниз) ===
  let disabledReasonKey: string | null = null;
  if (!dataset) {
    disabledReasonKey = null; // нечего показывать — parent сам не отрисует
  } else if (forceDisabled) {
    disabledReasonKey = 'files.results.download_tooltip_unavailable';
  } else if (isDownloading) {
    disabledReasonKey = null; // loading — другая визуальная семантика
  } else if (isTimeout) {
    disabledReasonKey = 'files.results.download_tooltip_timeout';
  } else if (dataset.status === 'parsing' || dataset.status === 'uploaded') {
    disabledReasonKey = 'files.results.download_tooltip_processing';
  } else if (dataset.status === 'failed') {
    disabledReasonKey = 'files.results.download_tooltip_failed';
  }

  const isDisabled = !dataset || isDownloading || disabledReasonKey !== null;

  // === Tooltip / aria-label ===
  // Default: "Скачать" — i18n. Если есть disabledReason — показываем причину.
  const defaultLabel = label ?? t('files.results.act_download', {
    defaultValue: 'Скачать',
  });
  const tooltip = disabledReasonKey
    ? t(disabledReasonKey, { defaultValue: defaultLabel })
    : defaultLabel;
  const ariaLabel = tooltip;

  // === Click handler — guard на disabled, без двойных кликов ===
  const handleClick = () => {
    if (isDisabled) return;
    void onDownload();
  };

  // === aria-busy — только когда действительно качаем ===
  // (disabled сам по себе НЕ делает aria-busy — SR читает «button disabled»)
  const ariaBusy = isDownloading;

  const sharedProps = {
    type: 'button' as const,
    onClick: handleClick,
    disabled: isDisabled,
    'aria-disabled': isDisabled || undefined,
    'aria-busy': ariaBusy || undefined,
    'aria-label': ariaLabel,
    title: tooltip,
    'data-testid': testId,
    className: cn(
      getVariantClass(variant),
      isDisabled && 'opacity-50 cursor-not-allowed',
      className,
    ),
  };

  const Icon = isDownloading ? Loader2 : Download;

  return (
    <button {...sharedProps}>
      <Icon
        className={cn(
          variant === 'compact' ? 'w-3 h-3' : 'w-4 h-4',
          isDownloading && 'animate-spin',
        )}
      />
      {variant !== 'compact' && (
        <span className={cn(variant === 'ghost' && 'hidden lg:inline')}>
          {defaultLabel}
        </span>
      )}
      {variant === 'compact' && (
        <span className="hidden lg:inline truncate max-w-[8rem]">
          {defaultLabel}
        </span>
      )}
      {/* SR-only error announcement — inline error обычно в parent */}
      {error && (
        <span className="sr-only" role="status">
          {error}
        </span>
      )}
    </button>
  );
}

function getVariantClass(variant: DownloadButtonVariant): string {
  switch (variant) {
    case 'primary':
      return 'btn-primary flex items-center gap-1.5';
    case 'ghost':
      return 'btn-ghost px-2 py-1 text-xs flex items-center gap-1';
    case 'compact':
      return cn(
        'text-xs px-2 py-1 rounded border flex items-center gap-1 transition-colors',
        'border-surface-700 text-surface-300 hover:border-surface-500',
      );
  }
}
